import type { Editor } from '@tiptap/core';

import { fieldReferencePattern, normalizeFieldLabel } from './field-chips';

type TextRange = { from: number; to: number };

// Each inline leaf, such as a line break, reads as one character so offsets stay positions.
const LEAF_TEXT = '\ufffc';

/**
 * Where the body first still reads `[[label]]`, or null when it doesn't. Each paragraph is read
 * whole, so a blank partly in bold is still found.
 */
export const findBlankField = (editor: Editor, label: string): TextRange | null => {
  let blank: TextRange | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (blank) return false;
    if (!node.isTextblock) return true;
    const text = node.textBetween(0, node.content.size, undefined, LEAF_TEXT);
    for (const match of text.matchAll(fieldReferencePattern)) {
      if (match.index === undefined || normalizeFieldLabel(match[1]) !== label) continue;
      const from = pos + 1 + match.index;
      blank = { from, to: from + match[0].length };
      break;
    }
    return false;
  });
  return blank;
};

/** Selects the blank, so what the agent types next replaces it. */
export const selectBlankField = (editor: Editor, blank: TextRange) =>
  editor.chain().focus().setTextSelection(blank).run();

const sentenceSegmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
// The segmenter ends a sentence at a title, because the name after it is capitalized.
const endsWithTitlePattern = /\b(?:Mr|Mrs|Ms|Dr|Prof)\.\s*$/;

/** The sentence holding the offset, with the spaces after it. */
const sentenceAround = (text: string, offset: number): TextRange => {
  let from = 0;
  for (const { segment, index } of sentenceSegmenter.segment(text)) {
    const to = index + segment.length;
    if (endsWithTitlePattern.test(segment)) continue;
    if (offset < to) return { from, to };
    from = to;
  }
  return { from, to: text.length };
};

/**
 * Removes the sentence holding the blank, with the space after. A paragraph left with nothing in
 * it goes too.
 */
export const removeBlankFieldSentence = (editor: Editor, blank: TextRange) => {
  const $blank = editor.state.doc.resolve(blank.from);
  const paragraph = $blank.parent;
  const paragraphStart = $blank.start();
  const text = paragraph.textBetween(0, paragraph.content.size, undefined, LEAF_TEXT);

  const sentence = sentenceAround(text, blank.from - paragraphStart);
  let sentenceStart = sentence.from;
  const sentenceStop = sentence.to;
  // The last sentence takes the space before it instead, so none is left trailing.
  if (sentenceStop === text.length) {
    while (sentenceStart > 0 && text[sentenceStart - 1] === ' ') sentenceStart -= 1;
  }

  const isWholeParagraph =
    text.slice(0, sentenceStart).trim() === '' && sentenceStop === text.length;
  if (isWholeParagraph && editor.state.doc.childCount > 1) {
    editor.chain().focus().deleteRange({ from: $blank.before(), to: $blank.after() }).run();
    return;
  }
  editor
    .chain()
    .focus()
    .deleteRange({ from: paragraphStart + sentenceStart, to: paragraphStart + sentenceStop })
    .run();
};
